import React from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight } from "react-icons/fi";
import { FilterOption } from "./libraryTypes";

type Props = {
  roots: FilterOption[];
  selected: string[];
  onToggle: (id: string) => void;
};

const isSelected = (selected: string[], id: string) => selected.includes(id);

/**
 * Client-portal category mega-menu: left rail of top-level groups, right grid
 * of mid-level headers with leaf types underneath. Only published 3D branches
 * are passed in, so empty catalog groups never appear.
 */
export const CategoryHierarchyMenu: React.FC<Props> = ({
  roots,
  selected,
  onToggle,
}) => {
  const { t } = useTranslation();
  const [activeRootId, setActiveRootId] = React.useState(roots[0]?.id);
  const activeRoot =
    roots.find((root) => root.id === activeRootId) ?? roots[0];
  const midLevel = activeRoot?.children ?? [];

  React.useEffect(() => {
    if (!roots.some((root) => root.id === activeRootId)) {
      setActiveRootId(roots[0]?.id);
    }
  }, [activeRootId, roots]);

  if (roots.length === 0) {
    return (
      <p className="px-3 py-4 text-xs text-gray-500">{t("productLibrary.noMatchingOptions")}</p>
    );
  }

  return (
    <div className="flex min-h-[220px] w-[min(520px,calc(100vw-48px))]">
      <div
        className="w-44 shrink-0 overflow-y-auto border-r border-gray-100 py-1"
        aria-label={t("productLibrary.topLevelCategories")}
      >
        {roots.map((root) => {
          const active = root.id === activeRoot?.id;
          return (
            <button
              key={root.id}
              type="button"
              onMouseEnter={() => setActiveRootId(root.id)}
              onFocus={() => setActiveRootId(root.id)}
              onClick={() => onToggle(root.id)}
              className={`flex w-full items-center justify-between px-3 py-3 text-left text-sm capitalize ${
                active
                  ? "bg-[#F3E9C8] font-semibold text-[#111]"
                  : "text-gray-700 hover:bg-[#F3E9C8]/50"
              } ${isSelected(selected, root.id) && !active ? "font-semibold" : ""}`}
              aria-current={active ? "true" : undefined}
            >
              <span>{root.name}</span>
              <FiChevronRight className="shrink-0 text-gray-400" size={12} />
            </button>
          );
        })}
      </div>
      <div
        className="grid min-h-0 flex-1 auto-rows-max grid-cols-2 content-start gap-x-4 gap-y-3 overflow-y-auto p-3 sm:grid-cols-3"
        aria-label={
          activeRoot ? t("productLibrary.subcategoriesOf", { name: activeRoot.name }) : t("productLibrary.subcategories")
        }
      >
        {midLevel.length === 0 && (
          <p className="col-span-full px-2 py-4 text-xs text-gray-500">
            {t("productLibrary.noSubcategories")}
          </p>
        )}
        {midLevel.map((mid) => (
          <div key={mid.id} className="min-w-0">
            <button
              type="button"
              onClick={() => onToggle(mid.id)}
              className={`mb-1 rounded px-2 py-1 text-left text-sm font-semibold capitalize ${
                isSelected(selected, mid.id)
                  ? "bg-gray-100 text-[#111]"
                  : "text-[#111] hover:font-bold"
              }`}
            >
              {mid.name}
            </button>
            <div>
              {(mid.children ?? []).map((leaf) => (
                <button
                  key={leaf.id}
                  type="button"
                  onClick={() => onToggle(leaf.id)}
                  className={`block w-full rounded px-2 py-1 text-left text-xs capitalize ${
                    isSelected(selected, leaf.id)
                      ? "bg-gray-100 font-semibold text-[#111]"
                      : "text-gray-500 hover:font-semibold hover:text-[#111]"
                  }`}
                >
                  {leaf.name}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
